<template>
  <div class="machinery-detail-page">
    <!-- Header with back button, logo and language switcher -->
    <header class="machinery-header">
      <div class="container">
        <button @click="goBack" class="back-btn">
          <i class="fas fa-arrow-left"></i>
          {{ locale === 'tr' ? 'Geri Dön' : 'Go Back' }}
        </button>
        
        <div class="header-logo" @click="goToHome">
          <img src="/images/aslan-energy-logo-hq.jpg" alt="Aslan Energy" class="header-logo-img" />
          <div class="header-logo-text">
            <span class="company-name">Aslan Energy</span>
          </div>
        </div>
        
        <div class="language-switcher">
          <button 
            @click="changeLanguage('tr')" 
            :class="{ active: locale === 'tr' }"
            class="lang-btn"
          >
            TR
          </button>
          <button 
            @click="changeLanguage('en')" 
            :class="{ active: locale === 'en' }"
            class="lang-btn"
          >
            EN
          </button>
        </div>
      </div>
    </header>

    <div class="page-content">
      <div class="container">
          <!-- Loading State -->
          <div v-if="loading" class="loading">
            <div class="loader"></div>
            <p>{{ locale === 'tr' ? 'Makine verisi yükleniyor...' : 'Loading machinery data...' }}</p>
          </div>
          
          <!-- Error State -->
          <div v-else-if="error" class="error">
            <div class="error-icon">⚠️</div>
            <h3>{{ locale === 'tr' ? 'Veri Yüklenemedi' : 'Failed to Load Data' }}</h3>
            <p>{{ error }}</p>
            <button @click="() => loadMachinery(machineId)" class="retry-btn">
              {{ locale === 'tr' ? 'Tekrar Dene' : 'Retry' }}
            </button>
          </div>        <!-- Machinery Details -->
        <div v-else class="machinery-details">
          <!-- Ana Makine Kartı -->
          <div class="main-machinery-card">
            <div class="image-section">
              <!-- Image Slider -->
              <div 
                class="image-slider-container"
                @mouseenter="handleMouseEnter"
                @mouseleave="handleMouseLeave"
              >
                <div class="image-slider">
                  <div 
                    v-for="(image, index) in getMachineImages()" 
                    :key="index"
                    class="slide"
                    :class="{ 'active': currentImageIndex === index }"
                  >
                    <img 
                      :src="image" 
                      :alt="`${machinery?.name_tr || machinery?.name_en || 'Makine'} - ${index + 1}`" 
                      class="machinery-image"
                      @error="handleImageError"
                    />
                  </div>
                </div>
                
                <!-- Slider Controls -->
                <div v-if="getMachineImages().length > 1" class="slider-controls">
                  <button 
                    @click="previousImage"
                    class="slider-btn prev-btn"
                    :disabled="currentImageIndex === 0"
                  >
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <button 
                    @click="nextImage"
                    class="slider-btn next-btn"
                    :disabled="currentImageIndex === getMachineImages().length - 1"
                  >
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
                
                <!-- Slider Indicators -->
                <div v-if="getMachineImages().length > 1" class="slider-indicators">
                  <span 
                    v-for="(image, index) in getMachineImages()" 
                    :key="index"
                    class="indicator"
                    :class="{ 'active': currentImageIndex === index }"
                    @click="setImage(index)"
                  ></span>
                </div>
              </div>
            </div>
            
            <div class="info-section">
              <div class="brand-badge">{{ machinery?.brand || 'EPIROC' }}</div>
              <h1 class="machine-title">{{ 
                locale === 'tr' 
                  ? (machinery?.name_tr || machinery?.name_en || 'Makine Adı') 
                  : (machinery?.name_en || machinery?.name_tr || 'Machine Name') 
              }}</h1>
              <p class="machine-category">{{ 
                locale === 'tr' 
                  ? (machinery?.category_tr || machinery?.category_en || 'Kategori') 
                  : (machinery?.category_en || machinery?.category_tr || 'Category') 
              }}</p>
              <p class="machine-description">{{ 
                locale === 'tr' 
                  ? (machinery?.description_tr || machinery?.description_en || 'Açıklama') 
                  : (machinery?.description_en || machinery?.description_tr || 'Description') 
              }}</p>
            </div>
          </div>

          <!-- Teknik Özellikler -->
          <div class="specifications-section" v-if="hasSpecifications && machinery">
            <h2>{{ locale === 'tr' ? 'Teknik Özellikler' : 'Technical Specifications' }}</h2>
            <div class="specs-grid">
              <div class="spec-item" v-if="machinery?.drilling_diameter">
                <span class="spec-label">{{ locale === 'tr' ? 'Delme Çapı:' : 'Drilling Diameter:' }}</span>
                <span class="spec-value">{{ machinery.drilling_diameter }}</span>
              </div>
              <div class="spec-item" v-if="machinery?.drilling_depth">
                <span class="spec-label">{{ locale === 'tr' ? 'Delme Derinliği:' : 'Drilling Depth:' }}</span>
                <span class="spec-value">{{ machinery.drilling_depth }}</span>
              </div>
              <div class="spec-item" v-if="machinery?.motor_power">
                <span class="spec-label">{{ locale === 'tr' ? 'Motor Gücü:' : 'Motor Power:' }}</span>
                <span class="spec-value">{{ machinery.motor_power }}</span>
              </div>
              <div class="spec-item" v-if="machinery?.weight">
                <span class="spec-label">{{ locale === 'tr' ? 'Ağırlık:' : 'Weight:' }}</span>
                <span class="spec-value">{{ machinery.weight }}</span>
              </div>
              <!-- JSON specifications -->
              <div 
                v-for="(value, key) in additionalSpecs" 
                :key="key" 
                class="spec-item"
              >
                <span class="spec-label">{{ formatSpecLabel(key) }}:</span>
                <span class="spec-value">{{ value }}</span>
              </div>
            </div>
          </div>

          <!-- Özellikler -->
          <div class="features-section" v-if="machineFeatures.length > 0">
            <h2>{{ locale === 'tr' ? 'Özellikler' : 'Features' }}</h2>
            <div class="features-grid">
              <div 
                v-for="(feature, index) in machineFeatures" 
                :key="index"
                class="feature-item"
              >
                <span class="feature-icon">✓</span>
                <span class="feature-text">{{ feature }}</span>
              </div>
            </div>
          </div>

          <!-- İletişim Çağrısı -->
          <div class="contact-section">
            <div class="contact-card">
              <h3>{{ locale === 'tr' ? 'Bu Makine İle İlgileniyor musunuz?' : 'Interested in This Machine?' }}</h3>
              <p>{{ locale === 'tr' ? 'Makine kiralası, satın alma veya teknik destek için bizimle iletişime geçin.' : 'Contact us for machine rental, purchase, or technical support.' }}</p>
              <div class="contact-buttons">
                <button class="contact-btn primary" @click="scrollToContact">
                  {{ locale === 'tr' ? 'İletişime Geç' : 'Get in Touch' }}
                </button>
                <button class="contact-btn secondary" @click="goToMachinery">
                  {{ locale === 'tr' ? 'Diğer Makineler' : 'Other Machines' }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Modern Footer -->
    <footer class="modern-footer">
      <div class="footer-container">
        <div class="footer-content">
          <div class="footer-section company-info">
            <div class="footer-logo" @click="goToHome">
              <img src="/images/aslan-energy-logo-hq.jpg" alt="Aslan Energy" class="footer-logo-img" />
              <div class="footer-logo-text">
                <span class="company-name">Aslan Energy</span>
              </div>
            </div>
            <p class="company-description">
              {{ locale === 'tr' 
                ? 'Aslan Energy olarak yenilenebilir enerji \u00e7\u00f6z\u00fcmleri ile s\u00fcrd\u00fcr\u00fclebilir gelece\u011fi in\u015fa ediyoruz.' 
                : 'As Aslan Energy, we are building a sustainable future with renewable energy solutions.' 
              }}
            </p>
            <div class="social-links">
              <a href="https://www.instagram.com/aslanenergy/" class="social-link" target="_blank"><i class="fab fa-instagram"></i></a>
              <a href="https://www.facebook.com/p/Aslan-Energy-100054519006747/" class="social-link" target="_blank"><i class="fab fa-facebook"></i></a>
              <a href="https://www.linkedin.com/company/aslan-energy-madenci%CC%87li%CC%87k-san-ve-ti%CC%87c-ltd-%C5%9Fti%CC%87" class="social-link" target="_blank"><i class="fab fa-linkedin"></i></a>
              <a href="https://www.youtube.com/@aslanenergy" class="social-link" target="_blank"><i class="fab fa-youtube"></i></a>
            </div>
          </div>

          <div class="footer-section quick-links">
            <h4>{{ locale === 'tr' ? 'H\u0131zl\u0131 Linkler' : 'Quick Links' }}</h4>
            <ul>
              <li><a href="/">{{ locale === 'tr' ? 'Ana Sayfa' : 'Home' }}</a></li>
              <li><a href="/?page=projects">{{ locale === 'tr' ? 'Projelerimiz' : 'Our Projects' }}</a></li>
              <li><a href="/#services">{{ locale === 'tr' ? 'Hizmetlerimiz' : 'Our Services' }}</a></li>
              <li><a href="/#about">{{ locale === 'tr' ? 'Hakk\u0131m\u0131zda' : 'About Us' }}</a></li>
            </ul>
          </div>
        </div>

        <div class="footer-bottom">
          <div class="footer-bottom-content">
            <p>&copy; 2025 Aslan Energy. {{ locale === 'tr' ? 'T\u00fcm haklar\u0131 sakl\u0131d\u0131r.' : 'All rights reserved.' }}</p>
            <div class="footer-links">
              <a href="#">{{ locale === 'tr' ? 'Gizlilik Politikas\u0131' : 'Privacy Policy' }}</a>
              <a href="#">{{ locale === 'tr' ? 'Kullan\u0131m \u015eartlar\u0131' : 'Terms of Service' }}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import ApiService from '../services/api.js'

// Props
const props = defineProps({
  locale: {
    type: String,
    default: 'tr'
  }
})

// Reactive locale from props or localStorage
const locale = ref(props.locale || localStorage.getItem('preferred-language') || 'tr')

// Emits
const emit = defineEmits(['navigate'])

// State
const machinery = ref({})
const loading = ref(true)
const error = ref(null)
const machineId = ref(null)
const currentImageIndex = ref(0)
const autoPlayInterval = ref(null)

// Get machine ID from URL
const getMachineIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('id')
}

// Load machinery data
const loadMachinery = async (id) => {
  if (!id || id === null || id === undefined) {
    error.value = 'Geçersiz makine ID\'si'
    loading.value = false
    return
  }
  
  loading.value = true
  error.value = null
  
  try {
    console.log('Loading machinery with ID:', id)
    
    const response = await ApiService.getMachineryById(id)
    console.log('Machinery response:', response)
    
    if (response && typeof response === 'object') {
      machinery.value = { ...response }
      currentImageIndex.value = 0 // Reset slider index
    } else {
      throw new Error('Makine bulunamadı')
    }
    
    loading.value = false
  } catch (e) {
    // console.error('Machinery loading error:', e)
    error.value = 'Makine verisi alınamadı.'
    loading.value = false
  }
}

// Computed properties
const hasSpecifications = computed(() => {
  if (!machinery.value || typeof machinery.value !== 'object') return false
  return Boolean(machinery.value.drilling_diameter || 
         machinery.value.drilling_depth || 
         machinery.value.motor_power || 
         machinery.value.weight || 
         (machinery.value.specifications && Object.keys(machinery.value.specifications).length > 0))
})

const additionalSpecs = computed(() => {
  if (!machinery.value || !machinery.value.specifications || typeof machinery.value.specifications !== 'object') {
    return {}
  }
  
  const mainFields = ['drilling_diameter', 'drilling_depth', 'motor_power', 'weight']
  const specs = {}
  
  Object.keys(machinery.value.specifications).forEach(key => {
    if (!mainFields.includes(key)) {
      specs[key] = machinery.value.specifications[key]
    }
  })
  
  return specs
})

const machineFeatures = computed(() => {
  if (!machinery.value || typeof machinery.value !== 'object') return []
  if (locale.value === 'tr') {
    return machinery.value.features_tr || machinery.value.features || []
  } else {
    return machinery.value.features_en || machinery.value.features || []
  }
})

// Methods
const handleImageError = (event) => {
  const machineName = event.target.alt || 'EPIROC Makina'
  event.target.src = `https://source.unsplash.com/800x600/?epiroc,${machineName}`
}

const formatSpecLabel = (key) => {
  const labelMapTr = {
    'drilling_diameter': 'Delme Çapı',
    'drilling_depth': 'Delme Derinliği', 
    'motor_power': 'Motor Gücü',
    'weight': 'Ağırlık',
    'fuel_capacity': 'Yakıt Kapasitesi',
    'hydraulic_pressure': 'Hidrolik Basınç'
  }
  
  const labelMapEn = {
    'drilling_diameter': 'Drilling Diameter',
    'drilling_depth': 'Drilling Depth', 
    'motor_power': 'Motor Power',
    'weight': 'Weight',
    'fuel_capacity': 'Fuel Capacity',
    'hydraulic_pressure': 'Hydraulic Pressure'
  }
  
  const labelMap = locale.value === 'tr' ? labelMapTr : labelMapEn
  return labelMap[key] || key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// Image slider methods
const getMachineImages = () => {
  // API'den gelen görselleri veya fallback görselleri döndür
  if (machinery.value && machinery.value.images && machinery.value.images.length > 0) {
    return machinery.value.images
  }
  
  // Test için örnek görseller
  const baseImage = machinery.value?.image_url || `https://source.unsplash.com/800x600/?epiroc,machine,${machinery.value?.id || 1}`
  
  const testImages = [
    baseImage,
    'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  ]
  
  return testImages
}

const previousImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--
  }
}

const nextImage = () => {
  const maxIndex = getMachineImages().length - 1
  if (currentImageIndex.value < maxIndex) {
    currentImageIndex.value++
  }
}

const setImage = (index) => {
  currentImageIndex.value = index
}

// Auto-play functionality
const startAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
  }
  
  autoPlayInterval.value = setInterval(() => {
    const images = getMachineImages()
    if (images.length > 1) {
      currentImageIndex.value = (currentImageIndex.value + 1) % images.length
    }
  }, 5000) // 5 saniye
}

const stopAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
}

const handleMouseEnter = () => {
  stopAutoPlay()
}

const handleMouseLeave = () => {
  startAutoPlay()
}

// Methods
const changeLanguage = (lang) => {
  locale.value = lang
  localStorage.setItem('preferred-language', lang)
  // Sayfayı yeniden yükle ki değişiklikler görünür olsun
  setTimeout(() => {
    window.location.reload()
  }, 100)
}

const goBack = () => {
  window.location.href = window.location.pathname
}

const goToHome = () => {
  window.location.href = '/'
}

const scrollToContact = () => {
  window.location.href = '/#contact'
}

const goToMachinery = () => {
  window.location.href = '/#machinery'
}

// Lifecycle
onMounted(() => {
  // Home header'\u0131n\u0131 gizle
  document.body.classList.add('machinery-detail-page-active')
  
  // Dil tercihini localStorage'dan al
  const savedLang = localStorage.getItem('preferred-language')
  if (savedLang) {
    locale.value = savedLang
  }
  
  // URL'den makine ID'sini al
  machineId.value = getMachineIdFromUrl()
  
  if (machineId.value) {
    loadMachinery(machineId.value).then(() => {
      // Makine yüklendikten sonra auto-play'i başlat
      nextTick(() => {
        startAutoPlay()
      })
    })
  } else {
    error.value = locale.value === 'tr' 
      ? 'Ge\u00e7ersiz makine ID\'si.' 
      : 'Invalid machine ID.'
    loading.value = false
  }
})

// Cleanup
onUnmounted(() => {
  // Home header'\u0131n\u0131 tekrar g\u00f6ster
  document.body.classList.remove('machinery-detail-page-active')
})
</script>

<style scoped>
/* Hide home page header when machinery detail is active */
:global(body.machinery-detail-page-active .custom-header),
:global(body.machinery-detail-page-active .sticky-header),
:global(body.machinery-detail-page-active .nav-container),
:global(body.machinery-detail-page-active .navbar),
:global(body.machinery-detail-page-active .main-header),
:global(body.machinery-detail-page-active header:not(.machinery-header)),
:global(body.machinery-detail-page-active .header),
:global(body.machinery-detail-page-active nav) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  top: -9999px !important;
  left: -9999px !important;
  z-index: -1 !important;
}

/* Remove any top spacing when machinery detail is active */
:global(body.machinery-detail-page-active) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

:global(body.machinery-detail-page-active .machinery-detail-page) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.machinery-detail-page {
  min-height: 100vh;
  background: #f8f9fa;
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header Styles - Matching ProjectDetail */
.machinery-header {
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  color: white;
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 20px rgba(26, 54, 93, 0.3);
}

.machinery-header .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  text-decoration: none;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateX(-2px);
}

.back-btn i {
  font-size: 0.8rem;
}

.language-switcher {
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.25rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.lang-btn {
  background: transparent;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  opacity: 0.7;
}

.lang-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

.lang-btn.active {
  background: linear-gradient(45deg, #ff6b35, #f7931e);
  color: white;
  opacity: 1;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
}

/* Header Logo */
.header-logo {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(56, 142, 60, 0.1);
  border: 2px solid rgba(56, 142, 60, 0.2);
  border-radius: 12px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.header-logo:hover {
  background: rgba(56, 142, 60, 0.15);
  border-color: rgba(56, 142, 60, 0.3);
  transform: translateX(-50%) translateY(-1px);
  box-shadow: 0 4px 15px rgba(56, 142, 60, 0.2);
}

.header-logo-img {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: contain;
  background: white;
  padding: 2px;
}

.header-logo-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.header-logo-text .company-name {
  font-size: 1rem;
  font-weight: 700;
  color: white;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Page Content */
.page-content {
  padding: 2rem 0;
}

/* Loading & Error States */
.loading, .error {
  text-align: center;
  margin: 4rem 0;
  background: white;
  padding: 3rem;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ff6b35;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.retry-btn {
  background: #ff6b35;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 1rem;
}

/* Main Machinery Card */
.main-machinery-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

.image-section {
  position: relative;
  height: 400px;
  overflow: hidden;
}

/* Image Slider Styles */
.image-slider-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-slider {
  position: relative;
  width: 100%;
  height: 100%;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.6s ease-in-out;
}

.slide.active {
  opacity: 1;
}

.machinery-image {
  width: 100%;
  height: 400px;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.machinery-image:hover {
  transform: scale(1.05);
}

/* Slider Controls */
.slider-controls {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 10;
}

.image-slider-container:hover .slider-controls {
  opacity: 1;
  pointer-events: auto;
}

.slider-btn {
  background: rgba(255, 255, 255, 0.95);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
}

.slider-btn:hover {
  background: white;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.slider-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.slider-btn i {
  color: #1a365d;
  font-size: 16px;
}

/* Slider Indicators */
.slider-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 10;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.indicator.active {
  background: #ff6b35;
  border-color: white;
  transform: scale(1.2);
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: scale(1.1);
}

.info-section {
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.brand-badge {
  background: linear-gradient(45deg, #ff6b35, #f7931e);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 1px;
  display: inline-block;
  margin-bottom: 1rem;
  width: fit-content;
}

.machine-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a365d;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.machine-category {
  color: #4a5568;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-style: italic;
}

.machine-description {
  color: #2d3748;
  font-size: 1.1rem;
  line-height: 1.6;
}

/* Specifications Section */
.specifications-section {
  background: white;
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.specifications-section h2 {
  color: #1a365d;
  font-size: 2rem;
  margin-bottom: 2rem;
  position: relative;
}

.specifications-section h2::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #ff6b35, #f7931e);
  border-radius: 2px;
}

.specs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border-left: 5px solid #ff6b35;
  transition: transform 0.2s ease;
}

.spec-item:hover {
  transform: translateY(-2px);
}

.spec-label {
  font-weight: 600;
  color: #4a5568;
  font-size: 1rem;
}

.spec-value {
  font-weight: 700;
  color: #1a365d;
  font-size: 1.1rem;
}

/* Features Section */
.features-section {
  background: white;
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.features-section h2 {
  color: #1a365d;
  font-size: 2rem;
  margin-bottom: 2rem;
  position: relative;
}

.features-section h2::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #ff6b35, #f7931e);
  border-radius: 2px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  gap: 1rem;
  transition: transform 0.2s ease;
}

.feature-item:hover {
  transform: translateY(-2px);
}

.feature-icon {
  color: #ff6b35;
  font-weight: bold;
  font-size: 1.3rem;
  width: 24px;
  text-align: center;
}

.feature-text {
  color: #2d3748;
  font-size: 1rem;
  font-weight: 500;
}

/* Contact Section */
.contact-section {
  margin-top: 3rem;
}

.contact-card {
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  color: white;
  padding: 3rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(26, 54, 93, 0.2);
}

.contact-card h3 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: white;
}

.contact-card p {
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.contact-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.contact-btn {
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 160px;
}

.contact-btn.primary {
  background: linear-gradient(45deg, #ff6b35, #f7931e);
  color: white;
}

.contact-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
}

.contact-btn.secondary {
  background: transparent;
  color: white;
  border: 2px solid #ff6b35;
}

.contact-btn.secondary:hover {
  background: rgba(255, 107, 53, 0.1);
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 968px) {
  .main-machinery-card {
    grid-template-columns: 1fr;
  }
  
  .machinery-image {
    height: 300px;
  }
  
  .image-section {
    height: 300px;
  }
  
  .slider-btn {
    width: 40px;
    height: 40px;
  }
  
  .slider-btn i {
    font-size: 14px;
  }
  
  .slider-controls {
    padding: 0 15px;
  }
  
  .indicator {
    width: 10px;
    height: 10px;
  }
  
  .info-section {
    padding: 2rem;
  }
  
  .machine-title {
    font-size: 2rem;
  }
  
  .machinery-header .container {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .header-logo {
    order: -1;
    padding: 0.4rem 0.8rem;
  }
  
  .back-btn {
    align-self: flex-start;
  }
  
  .language-switcher {
    align-self: flex-end;
  }
}

@media (max-width: 768px) {
  .machinery-header {
    padding: 0.6rem 0;
  }
  
  .machinery-header .container {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0 1rem;
  }
  
  .header-logo {
    position: relative;
    left: auto;
    transform: none;
    order: 0;
    padding: 0.3rem 0.6rem;
  }
  
  .header-logo img {
    height: 24px;
  }
  
  .header-logo-text {
    font-size: 0.8rem;
  }
  
  .back-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
  
  .lang-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    min-width: 35px;
  }
  
  .language-switcher {
    gap: 0.3rem;
  }
  
  .container {
    padding: 0 15px;
  }
  
  .page-content {
    padding: 1rem 0;
  }
  
  .specifications-section,
  .features-section {
    padding: 2rem;
  }
  
  .specs-grid, .features-grid {
    grid-template-columns: 1fr;
  }
  
  .contact-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .breadcrumb {
    display: none;
  }
}

@media (max-width: 480px) {
  .machine-title {
    font-size: 1.5rem;
  }
  
  .specifications-section h2,
  .features-section h2 {
    font-size: 1.5rem;
  }
  
  .contact-card {
    padding: 2rem;
  }
}

/* Modern Footer */
.modern-footer {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  padding: 4rem 0 0;
  margin-top: 2rem;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 4rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background: white;
  border: 2px solid #ff6b35;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: fit-content;
}

.footer-logo:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
  border-color: #f7931e;
}

.footer-logo-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: contain;
}

.footer-logo-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.footer-logo-text .company-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #ff6b35;
  line-height: 1;
}

.footer-section h4 {
  color: #ff6b35;
  margin-bottom: 1.5rem;
  font-size: 1.4rem;
  font-weight: 700;
}

.company-description {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.2rem;
}

.social-link:hover {
  background: #ff6b35;
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(255, 107, 53, 0.4);
}

.quick-links ul {
  list-style: none;
  padding: 0;
}

.quick-links li {
  margin-bottom: 0.8rem;
}

.quick-links a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 1rem;
}

.quick-links a:hover {
  color: #ff6b35;
}

.footer-bottom {
  padding: 2rem 0;
  background: rgba(0, 0, 0, 0.2);
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.7);
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: #ff6b35;
}

/* Footer Responsive Design */
@media (max-width: 768px) {
  .modern-footer {
    padding: 3rem 0 0;
    margin-top: 1.5rem;
  }
  
  .footer-container {
    padding: 0 1rem;
  }
  
  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  .footer-logo {
    margin: 0 auto 1.5rem auto;
    padding: 10px 14px;
  }
  
  .footer-logo-img {
    width: 40px;
    height: 40px;
  }
  
  .footer-logo-text .company-name {
    font-size: 1.1rem;
  }
  
  .footer-section h4 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
  
  .footer-bottom-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .footer-links {
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .footer-container {
    padding: 0 0.5rem;
  }
  
  .footer-content {
    gap: 1.5rem;
  }
  
  .footer-logo {
    padding: 8px 12px;
    border-radius: 10px;
  }
  
  .footer-logo-img {
    width: 35px;
    height: 35px;
  }
  
  .footer-logo-text .company-name {
    font-size: 1rem;
  }
  
  .footer-section h4 {
    font-size: 1.1rem;
  }
}
</style>